import React from 'react';
import styled from '@emotion/styled';
import Button from '@via-profit/ui-kit/Button';
import { useTheme } from '@via-profit/ui-kit/ThemeProvider';

import type { Place } from '../../api/place';

export interface PlacePopupProps {
  /** null - адрес ещё определяется. */
  readonly place: Place | null;
  readonly onRoute: (place: Place) => void;
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 200px;
  font-size: 13px;
  line-height: 1.3;
`;

const Title = styled.div`
  padding-right: 12px;
  font-size: 15px;
  font-weight: 700;
  overflow-wrap: break-word;
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.color.textSecondary.toString()};
  overflow-wrap: break-word;
`;

const Actions = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 6px;
  font-size: 13px;
`;

const Coords = styled.span`
  font-variant-numeric: tabular-nums;
`;

/** "55.751244, 37.618423" - широта, долгота, как в Яндекс- и Google-картах. */
const formatCoords = ([lat, lon]: readonly [number, number]): string => `${lat.toFixed(6)}, ${lon.toFixed(6)}`;

async function copyText(text: string): Promise<void> {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  // clipboard API есть только на https и localhost - запасной путь для http
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  try {
    if (!document.execCommand('copy')) throw new Error('copy command was rejected');
  } finally {
    textarea.remove();
  }
}

const PlacePopup: React.FC<PlacePopupProps> = ({ place, onRoute }) => {
  const theme = useTheme();
  const [copied, setCopied] = React.useState(false);
  const coords = place ? formatCoords(place.marker) : '';

  // новое место - кнопка снова "копировать"
  React.useEffect(() => setCopied(false), [coords]);
  React.useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopy = () => {
    copyText(coords)
      .then(() => setCopied(true))
      .catch((error: unknown) => console.error('Copy failed:', error));
  };

  return (
    <Body>
      {place ? (
        <>
          <Title>{place.title}</Title>
          {place.subtitle && <Muted>{place.subtitle}</Muted>}
          <Actions>
            <Button type="button" variant="standard" color="primary" onClick={() => onRoute(place)}>
              Маршрут
            </Button>
            <Button
              type="button"
              variant="outlined"
              color={copied ? theme.color.success.toString() : 'default'}
              title="Скопировать координаты"
              onClick={handleCopy}
            >
              <Coords>{copied ? 'Скопировано ✓' : coords}</Coords>
            </Button>
          </Actions>
        </>
      ) : (
        <Muted>Определяем адрес…</Muted>
      )}
    </Body>
  );
};

export default PlacePopup;
